// src/modules/purityAnalyzer/pages/CalibrationHistory.jsx
// Full calibration timeline with activate / rollback / archive / compare /
// audit-trail actions. Append-only history — nothing is ever deleted.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Timeline, Tag, Typography, Space, Button, Select, message, Drawer,
  Descriptions, Popconfirm, Empty, Spin, Row, Col, Statistic,
} from 'antd';
import {
  HistoryOutlined, CheckCircleOutlined, SwapOutlined, InboxOutlined,
  DiffOutlined, RollbackOutlined, PlusOutlined, FireOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  fetchCalibrations, activateCalibration, rollbackCalibration,
  archiveCalibration, compareCalibrations, fetchCalibrationAudit,
} from '../api';
import useBranchScope from '../hooks/useBranchScope';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  CALIBRATION_STATUS_COLORS, FLUID_CONDITION_META,
} from '../constants/purityConstants';

const { Title, Text } = Typography;

export default function CalibrationHistory() {
  const navigate = useNavigate();
  const { isHO, branches } = useBranchScope();
  const { can } = usePermissions();
  const canActivate = can('purityAnalyzer', 'activateCalibration');
  const canArchive = can('purityAnalyzer', 'deleteCalibration');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchId, setBranchId] = useState();
  const [selection, setSelection] = useState([]); // for compare (max 2)
  const [compare, setCompare] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCalibrations({ branchId, page: 1, limit: 100 });
      setRows(res?.data?.data?.items || []);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, id, verb) => {
    try {
      await fn(id);
      message.success(`Calibration ${verb}`);
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || `Failed to ${verb}`);
    }
  };

  const toggleSelect = (id) =>
    setSelection((sel) =>
      sel.includes(id) ? sel.filter((s) => s !== id) : [...sel.slice(-1), id]
    );

  const runCompare = async () => {
    try {
      const res = await compareCalibrations(selection[0], selection[1]);
      setCompare(res?.data?.data || null);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Compare failed');
    }
  };

  const openAudit = async (cal) => {
    setAuditFor(cal);
    try {
      const res = await fetchCalibrationAudit(cal._id);
      setAuditRows(res?.data?.data || []);
    } catch { setAuditRows([]); }
  };

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <HistoryOutlined style={{ marginRight: 10 }} />Calibration History
        </Title>
        <Space wrap>
          {isHO && (
            <Select allowClear placeholder="All branches" style={{ width: 180 }}
              options={branches.map((b) => ({ value: b._id, label: b.name }))}
              onChange={setBranchId} />
          )}
          <Button icon={<DiffOutlined />} disabled={selection.length !== 2} onClick={runCompare}>
            Compare ({selection.length}/2)
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/purity/calibrate')}>
            New Calibration
          </Button>
        </Space>
      </Space>

      <Spin spinning={loading}>
        {rows.length === 0 && !loading ? (
          <Empty description="No calibrations yet — record the first copper calibration." />
        ) : (
          <Timeline
            mode="left"
            items={rows.map((cal) => {
              const fluid = FLUID_CONDITION_META[cal.fluidCondition] || FLUID_CONDITION_META.STABLE;
              const isActive = cal.status === 'ACTIVE';
              return {
                color: isActive ? 'green' : cal.status === 'ARCHIVED' ? 'gray' : 'blue',
                dot: isActive ? <CheckCircleOutlined style={{ fontSize: 16 }} /> : undefined,
                children: (
                  <Card
                    size="small"
                    style={{
                      marginBottom: 4,
                      borderColor: isActive ? '#52c41a' : undefined,
                      cursor: 'pointer',
                      outline: selection.includes(cal._id) ? '2px solid #1677ff' : 'none',
                    }}
                    onClick={() => toggleSelect(cal._id)}
                  >
                    <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap align="start">
                      <div>
                        <Space wrap>
                          <Text strong>{cal.calibrationNumber}</Text>
                          <Tag color={CALIBRATION_STATUS_COLORS[cal.status]}>{cal.status}</Tag>
                          <Tag color={fluid.color} icon={<FireOutlined />}>{fluid.label}</Tag>
                          {isHO && cal.branch && <Tag>{cal.branch.name}</Tag>}
                          {cal.machine && <Tag color="purple">{cal.machine.machineCode}</Tag>}
                        </Space>
                        <div style={{ marginTop: 6 }}>
                          <Space size={16} wrap>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Diesel <Text strong>{cal.dieselDensity}</Text> g/cm³
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Temp index <Text strong>{cal.temperatureIndex}</Text>%
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Cu air <Text strong>{cal.copperWeightAir}</Text> g
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Cu diesel <Text strong>{cal.copperWeightDiesel}</Text> g
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Δ <Text strong>{cal.weightDifference}</Text> g
                            </Text>
                          </Space>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {cal.operator?.name || '—'} · {dayjs(cal.createdAt).format('DD MMM YYYY · HH:mm')}
                          {cal.notes ? ` · ${cal.notes}` : ''}
                        </Text>
                      </div>
                      <Space onClick={(e) => e.stopPropagation()} wrap>
                        <Button size="small" icon={<HistoryOutlined />} onClick={() => openAudit(cal)}>
                          Audit
                        </Button>
                        {canActivate && !isActive && cal.status === 'INACTIVE' && (
                          <Button size="small" type="primary" icon={<SwapOutlined />}
                            onClick={() => act(activateCalibration, cal._id, 'activated')}>
                            Activate
                          </Button>
                        )}
                        {canActivate && cal.status === 'ARCHIVED' && (
                          <Popconfirm title="Roll back to this calibration?"
                            onConfirm={() => act(rollbackCalibration, cal._id, 'rolled back')}>
                            <Button size="small" icon={<RollbackOutlined />}>Rollback</Button>
                          </Popconfirm>
                        )}
                        {canArchive && !isActive && cal.status !== 'ARCHIVED' && (
                          <Popconfirm title="Archive this calibration? (kept in history forever)"
                            onConfirm={() => act(archiveCalibration, cal._id, 'archived')}>
                            <Button size="small" icon={<InboxOutlined />}>Archive</Button>
                          </Popconfirm>
                        )}
                      </Space>
                    </Space>
                  </Card>
                ),
              };
            })}
          />
        )}
      </Spin>

      {/* ── Compare drawer ── */}
      <Drawer title="Compare Calibrations" open={!!compare} onClose={() => setCompare(null)} width={560}>
        {compare && (
          <>
            <Row gutter={16}>
              {[compare.a, compare.b].map((c) => (
                <Col span={12} key={c._id}>
                  <Card size="small" title={c.calibrationNumber}>
                    <Statistic title="Diesel Density" value={c.dieselDensity} precision={5} />
                    <Statistic title="Temp Index (%)" value={c.temperatureIndex} precision={3} />
                    <Statistic title="Cu Air (g)" value={c.copperWeightAir} precision={4} />
                    <Statistic title="Cu Diesel (g)" value={c.copperWeightDiesel} precision={4} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(c.createdAt).format('DD MMM YYYY HH:mm')}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
            <Card size="small" title="Deltas (B − A)" style={{ marginTop: 12 }}>
              <Descriptions column={1} size="small">
                {Object.entries(compare.deltas).map(([k, v]) => (
                  <Descriptions.Item key={k} label={k}>
                    <Text strong type={v === 0 ? 'secondary' : v > 0 ? 'success' : 'danger'}>
                      {v > 0 ? '+' : ''}{v}
                    </Text>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          </>
        )}
      </Drawer>

      {/* ── Audit drawer ── */}
      <Drawer title={`Audit Trail — ${auditFor?.calibrationNumber || ''}`}
        open={!!auditFor} onClose={() => setAuditFor(null)} width={440}>
        <Timeline
          items={auditRows.map((row) => ({
            children: (
              <div>
                <Text strong>{row.action.replace(/_/g, ' ')}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.performedBy?.name || 'System'} · {dayjs(row.createdAt).format('DD MMM YYYY HH:mm:ss')}
                  </Text>
                </div>
              </div>
            ),
          }))}
        />
        {!auditRows.length && <Text type="secondary">No audit entries.</Text>}
      </Drawer>
    </div>
  );
}
